import { useState } from 'react';
import { useNutritionStore, DietType, DayPlan } from '@/store/nutritionStore';
import { Plus, Droplets, Flame, Apple, Sparkles, X, Lock, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TextureButton } from '@/components/ui/TextureButton';
import { useOnboardingStore } from '@/store/onboardingStore';
import { generateNutritionPlan } from '@/services/ai/NutritionAI';
import { PageShell } from '@/components/ui/PageShell';
import { AnalysisTimeline } from '@/components/ui/AnalysisTimeline';

export function NutritionPage() {
    const {
        today, dietType, restrictions, weeklyPlan, isGenerating, aiUsage, isPro,
        setDietType, logWater, setRestrictions, setWeeklyPlan, setGenerating, incrementAiUsage, setPro
    } = useNutritionStore();
    const targets = useNutritionStore(s => s.targetMacros);
    const targetCals = useNutritionStore(s => s.targetCalories);
    const { profile } = useOnboardingStore();

    const [activeTab, setActiveTab] = useState<'daily' | 'diet' | 'ai'>('daily');
    const [restrictionInput, setRestrictionInput] = useState('');
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [showAnalysis, setShowAnalysis] = useState(false);

    // Calculate Percentages
    const calProgress = Math.min((today.calories / targetCals) * 100, 100);
    const waterProgress = Math.min((today.waterOz / 120) * 100, 100);

    const handleAddRestriction = () => {
        if (restrictionInput.trim()) {
            setRestrictions([...restrictions, restrictionInput.trim()]);
            setRestrictionInput('');
        }
    };

    const handleRemoveRestriction = (res: string) => {
        setRestrictions(restrictions.filter(r => r !== res));
    };

    const handleGenerate = async () => {
        // Usage Check (Day Rollover Logic)
        const todayStr = new Date().toISOString().split('T')[0];
        const isNewDay = aiUsage.lastUsed !== todayStr;
        const currentCount = isNewDay ? 0 : aiUsage.count;

        if (!isPro && currentCount >= 1) {
            setShowUpgrade(true);
            return;
        }

        setShowAnalysis(true);
    };

    const executeGeneration = async () => {
        setGenerating(true);
        try {
            // Mock response for immediate feedback if no API key
            const mockPlan: DayPlan[] = [
                {
                    day: "Day 1",
                    meals: {
                        breakfast: { name: "Oatmeal & Berries", calories: 450, macros: { protein: 15, carbs: 60, fats: 10 }, description: "Steel cut oats with blueberries and walnuts." },
                        lunch: { name: "Grilled Chicken Salad", calories: 600, macros: { protein: 50, carbs: 20, fats: 25 }, description: "Chicken breast, mixed greens, olive oil dressing." },
                        dinner: { name: "Salmon & Asparagus", calories: 700, macros: { protein: 45, carbs: 10, fats: 35 }, description: "Baked salmon with roasted asparagus." },
                        snack: { name: "Greek Yogurt", calories: 200, macros: { protein: 20, carbs: 10, fats: 5 }, description: "Plain greek yogurt with honey." }
                    },
                    totalCalories: 1950,
                    totalMacros: { protein: 130, carbs: 100, fats: 75 }
                },
                {
                    day: "Day 2",
                    meals: {
                        breakfast: { name: "Eggs & Avo Toast", calories: 500, macros: { protein: 25, carbs: 30, fats: 20 }, description: "Scrambled eggs on sourdough." },
                        lunch: { name: "Turkey Wrap", calories: 550, macros: { protein: 40, carbs: 40, fats: 15 }, description: "Whole wheat wrap with turkey and swiss." },
                        dinner: { name: "Steak & Sweet Potato", calories: 800, macros: { protein: 60, carbs: 50, fats: 30 }, description: "Ribeye steak with baked sweet potato." },
                        snack: { name: "Protein Shake", calories: 150, macros: { protein: 25, carbs: 5, fats: 2 }, description: "Whey protein with water." }
                    },
                    totalCalories: 2000,
                    totalMacros: { protein: 150, carbs: 125, fats: 67 }
                },
                {
                    day: "Day 3",
                    meals: {
                        breakfast: { name: "Smoothie Bowl", calories: 400, macros: { protein: 20, carbs: 60, fats: 8 }, description: "Acai base with granola." },
                        lunch: { name: "Beef & Rice Bowl", calories: 700, macros: { protein: 50, carbs: 80, fats: 20 }, description: "Ground beef with jasmine rice." },
                        dinner: { name: "Chicken Stir Fry", calories: 650, macros: { protein: 45, carbs: 60, fats: 15 }, description: "Chicken and veggies in soy sauce." },
                        snack: { name: "Almonds", calories: 200, macros: { protein: 6, carbs: 6, fats: 15 }, description: "Handful of raw almonds." }
                    },
                    totalCalories: 1950,
                    totalMacros: { protein: 121, carbs: 206, fats: 58 }
                }
            ];

            // Try real call first
            try {
                const realPlan = await generateNutritionPlan(profile as any, dietType, restrictions);
                setWeeklyPlan(realPlan);
                incrementAiUsage();
            } catch (e) {
                console.warn("Falling back to mock plan", e);
                setWeeklyPlan(mockPlan);
                incrementAiUsage(); // Count mocks too for demo? Or maybe not. Let's count for now to simulate limits.
            }

        } catch (error) {
            console.error(error);
        } finally {
            setGenerating(false);
            setShowAnalysis(false);
        }
    };

    const handleUpgrade = () => {
        setPro(true);
        setShowUpgrade(false);
    };

    return (
        <PageShell title="Nutrition" subtitle="Eat to perform." right={<Apple className="w-6 h-6 text-green-500" />}>
            {/* Main Tabs */}
            <div className="px-4 mb-6">
                <div className="flex p-1 bg-bg-surface rounded-xl">
                    <button
                        onClick={() => setActiveTab('daily')}
                        className={cn(
                            "flex-1 py-2 text-sm font-bold rounded-lg transition-all",
                            activeTab === 'daily' ? "bg-bg-elevated text-white shadow-sm" : "text-text-muted hover:text-text-secondary"
                        )}
                    >
                        Daily
                    </button>
                    <button
                        onClick={() => setActiveTab('diet')}
                        className={cn(
                            "flex-1 py-2 text-sm font-bold rounded-lg transition-all",
                            activeTab === 'diet' ? "bg-bg-elevated text-white shadow-sm" : "text-text-muted hover:text-text-secondary"
                        )}
                    >
                        Framework
                    </button>
                    <button
                        onClick={() => setActiveTab('ai')}
                        className={cn(
                            "flex-1 py-2 text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-1",
                            activeTab === 'ai' ? "bg-bg-elevated text-white shadow-sm" : "text-text-muted hover:text-text-secondary"
                        )}
                    >
                        <Sparkles size={14} className={activeTab === 'ai' ? "text-purple-400" : ""} />
                        AI Plan
                    </button>
                </div>
            </div>

            {activeTab === 'daily' && (
                <div className="px-4 space-y-6 animate-fade-in">
                    {/* 1. Hero Ring - Calories */}
                    <div className="flex justify-center py-4">
                        <div className="relative w-48 h-48">
                            {/* Background Glow */}
                            <div className="absolute inset-0 bg-green-500/5 blur-2xl rounded-full" />

                            <svg className="w-full h-full transform -rotate-90">
                                <circle cx="96" cy="96" r="88" stroke="#1C2430" strokeWidth="12" fill="none" />
                                <circle
                                    cx="96" cy="96" r="88"
                                    stroke="#10B981" strokeWidth="12" fill="none"
                                    strokeDasharray={2 * Math.PI * 88}
                                    strokeDashoffset={2 * Math.PI * 88 * (1 - calProgress / 100)}
                                    strokeLinecap="round"
                                    className="transition-all duration-1000 ease-out"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-bold text-white">{today.calories}</span>
                                <span className="text-xs text-text-muted uppercase tracking-wider">/ {targetCals} kcal</span>
                            </div>
                        </div>
                    </div>

                    {/* 2. Macro Grid */}
                    <div className="grid grid-cols-3 gap-3">
                        <MacroCard label="Protein" val={today.macros.protein} target={targets.protein} color="text-blue-400" />
                        <MacroCard label="Carbs" val={today.macros.carbs} target={targets.carbs} color="text-green-400" />
                        <MacroCard label="Fats" val={today.macros.fats} target={targets.fats} color="text-yellow-400" />
                    </div>

                    {/* 3. Water Tracker */}
                    <div className="glass-panel p-5 rounded-2xl relative overflow-hidden">
                        <div className="flex justify-between items-center z-10 relative">
                            <div>
                                <h3 className="text-white font-bold flex items-center gap-2">
                                    <Droplets className="text-blue-500" size={18} />
                                    Hydration
                                </h3>
                                <p className="text-xs text-text-muted">{today.waterOz} / 120 oz</p>
                            </div>
                            <button
                                onClick={() => logWater(8)}
                                className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center hover:bg-blue-500/40 transition-colors"
                            >
                                <Plus size={16} />
                            </button>
                        </div>

                        {/* Liquid Fill Animation */}
                        <div
                            className="absolute bottom-0 left-0 right-0 bg-blue-500/10 transition-all duration-700 ease-out"
                            style={{ height: `${waterProgress}%` }}
                        />
                    </div>
                </div>
            )}

            {activeTab === 'diet' && (
                <div className="px-4 space-y-4 animate-fade-in">
                    <h3 className="text-white font-bold mb-4">Select Framework</h3>

                    <DietOption
                        type="balanced"
                        current={dietType}
                        desc="Standard athletic mix. 40/30/30 split."
                        onClick={setDietType}
                    />
                    <DietOption
                        type="paleo"
                        current={dietType}
                        desc="Caveman style. Meat, fish, veg, fruit. No grains."
                        onClick={setDietType}
                    />
                    <DietOption
                        type="keto"
                        current={dietType}
                        desc="High fat, low carb. Metabolic flexibility."
                        onClick={setDietType}
                    />
                    <DietOption
                        type="carnivore"
                        current={dietType}
                        desc="Animal products only. Elimination diet."
                        onClick={setDietType}
                    />
                </div>
            )}

            {activeTab === 'ai' && (
                <div className="px-4 space-y-6 animate-fade-in">
                    {/* Usage Banner - if not Pro */}
                    {!isPro && (
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 flex justify-between items-center">
                            <span className="text-xs text-blue-300">Free Tier: {aiUsage.count}/1 Generations today</span>
                            <button onClick={() => setShowUpgrade(true)} className="text-xs font-bold text-gold-400 hover:underline">
                                Upgrade Plan
                            </button>
                        </div>
                    )}

                    {/* Restrictions Input */}
                    <div className="space-y-3">
                        <h3 className="text-white font-bold">Dietary Restrictions</h3>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={restrictionInput}
                                onChange={(e) => setRestrictionInput(e.target.value)}
                                placeholder="e.g. Dairy Free, No Peanuts..."
                                className="bg-bg-surface border border-white/10 rounded-lg px-4 py-2 text-sm text-white flex-1 focus:border-blue-500 focus:outline-none"
                                onKeyDown={(e) => e.key === 'Enter' && handleAddRestriction()}
                            />
                            <button
                                onClick={handleAddRestriction}
                                className="bg-blue-600 rounded-lg px-4 text-white hover:bg-blue-500"
                            >
                                <Plus size={18} />
                            </button>
                        </div>

                        {/* Chips */}
                        <div className="flex flex-wrap gap-2">
                            {restrictions.map((res) => (
                                <div key={res} className="bg-white/5 border border-white/10 rounded-full px-3 py-1 flex items-center gap-2 text-xs text-white animate-fade-in">
                                    {res}
                                    <button onClick={() => handleRemoveRestriction(res)} className="text-text-muted hover:text-red-400">
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Generate Button */}
                    <TextureButton
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className={cn("w-full", isGenerating ? "opacity-70" : "")}
                    >
                        {isGenerating ? (
                            <span className="flex items-center gap-2"><Sparkles className="animate-spin" size={16} /> Generating Plan...</span>
                        ) : (
                            <span className="flex items-center gap-2"><Sparkles size={16} /> Generate Weekly Plan</span>
                        )}
                    </TextureButton>

                    {/* AI Plan Display */}
                    {weeklyPlan && (
                        <div className="space-y-4">
                            <h3 className="text-white font-bold">Recommended Meals</h3>
                            {weeklyPlan.map((day, i) => (
                                <div key={i} className="glass-panel p-4 rounded-xl border border-white/5 animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="font-bold text-gold-400">{day.day}</h4>
                                        <span className="text-xs text-text-muted">{day.totalCalories} kcal</span>
                                    </div>

                                    <div className="space-y-3">
                                        <MealRow type="Breakfast" meal={day.meals.breakfast} />
                                        <MealRow type="Lunch" meal={day.meals.lunch} />
                                        <MealRow type="Dinner" meal={day.meals.dinner} />
                                        <MealRow type="Snack" meal={day.meals.snack} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Upgrade Modal */}
            {showUpgrade && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
                    <div className="bg-bg-card border border-gold-500/30 rounded-2xl p-6 max-w-sm w-full relative shadow-[0_0_50px_rgba(212,175,55,0.1)]">
                        <button
                            onClick={() => setShowUpgrade(false)}
                            className="absolute top-4 right-4 text-text-muted hover:text-white"
                        >
                            <X size={20} />
                        </button>

                        <div className="flex flex-col items-center text-center space-y-4 mb-6">
                            <div className="w-16 h-16 bg-gold-500/10 rounded-full flex items-center justify-center text-gold-500 mb-2">
                                <Lock size={32} />
                            </div>
                            <h2 className="text-2xl font-bold text-white">Unlock Pro Access</h2>
                            <p className="text-text-muted text-sm">
                                You've reached your daily free limit. Upgrade to Pro for unlimited AI nutrition plans optimized for your performance.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 bg-bg-surface rounded-lg">
                                <Zap className="text-gold-500" size={20} />
                                <div className="text-left">
                                    <div className="text-white font-bold text-sm">Unlimited Generations</div>
                                    <div className="text-text-muted text-[10px]">Create plans whenever you want</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-surface rounded-lg">
                                <Sparkles className="text-purple-400" size={20} />
                                <div className="text-left">
                                    <div className="text-white font-bold text-sm">Advanced Models</div>
                                    <div className="text-text-muted text-[10px]">Access to Gemini Pro Analysis</div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleUpgrade}
                            className="w-full mt-6 bg-gold-500 text-black font-bold py-3 rounded-lg hover:bg-gold-400 transition-colors shadow-lg"
                        >
                            Upgrade for $9.99/mo
                        </button>
                    </div>
                </div>
            )}

            <AnalysisTimeline
                isVisible={showAnalysis}
                onComplete={executeGeneration}
                title="Nutrition Engine"
                steps={[
                    { id: '1', label: 'EVALUATING METABOLIC LOAD', durationMs: 1500 },
                    { id: '2', label: 'ANALYZING MACRONUTRIENT RATIOS', durationMs: 2000 },
                    { id: '3', label: 'SYNTHESIZING MEAL STRUCTURE', durationMs: 2000 },
                    { id: '4', label: 'COMPILING WEEKLY PLAN', durationMs: 1500 },
                ]}
            />
        </PageShell>
    );
}

function MacroCard({ label, val, target, color }: { label: string, val: number, target: number, color: string }) {
    return (
        <div className="bg-bg-surface border border-white/5 p-3 rounded-xl text-center">
            <div className={cn("text-xs font-bold uppercase mb-1", color)}>{label}</div>
            <div className="text-lg font-bold text-white">{val}g</div>
            <div className="text-[10px] text-text-muted">/ {target}g</div>
        </div>
    );
}

function DietOption({ type, current, desc, onClick }: { type: DietType, current: DietType, desc: string, onClick: (t: DietType) => void }) {
    const isSelected = type === current;
    return (
        <button
            onClick={() => onClick(type)}
            className={cn(
                "w-full text-left p-4 rounded-xl border transition-all relative overflow-hidden group",
                isSelected
                    ? "bg-gold-500/10 border-gold-500 text-white shadow-[0_0_20px_rgba(212,175,55,0.1)]"
                    : "bg-bg-surface border-white/5 text-text-muted hover:border-white/20"
            )}
        >
            <div className="flex justify-between items-center relative z-10">
                <div>
                    <div className={cn("font-bold text-lg capitalize", isSelected ? "text-gold-400" : "text-white")}>
                        {type}
                    </div>
                    <div className="text-xs mt-1 opacity-80">{desc}</div>
                </div>
                {isSelected && <div className="text-gold-500"><Flame size={20} /></div>}
            </div>
        </button>
    );
}

function MealRow({ type, meal }: { type: string, meal: any }) {
    return (
        <div className="flex gap-3">
            <div className="w-16 text-[10px] font-bold text-text-muted uppercase pt-1">{type}</div>
            <div className="flex-1">
                <div className="text-sm font-bold text-white">{meal.name}</div>
                <div className="text-xs text-text-secondary line-clamp-1">{meal.description}</div>
            </div>
        </div>
    );
}
